import { useState } from 'react'

import examplesStamp from '@/assets/examples-stamp.png'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { AiAnalysisLoading } from '@/routes/Public/AiAnalysisLoading'
import { type CarrierCard } from '@/routes/Public/carrierCard'
import { CarrierResultCard } from '@/routes/Public/CarrierResultCard'
import { EXAMPLE_CARDS, withRangeMeta } from './getEstimatesData'

export type ResultsPhase = 'example' | 'loading' | 'results'

/**
 * The right column (desktop) / lower section (mobile). Holds the three states the
 * flow moves through: example results on land + during confirm, the analysis
 * loading beat after confirm, then the real result cards.
 */
export function ResultsPanel({
  phase,
  onAnalysisDone,
  cards,
  rangeBounds,
  pendingCount,
  exampleDimmed,
  exampleExiting,
}: {
  phase: ResultsPhase
  onAnalysisDone: () => void
  /** Real, generated result cards (results phase). */
  cards: CarrierCard[]
  rangeBounds: { min: number; max: number } | null
  /** Carriers still resolving — surfaced under the cards. */
  pendingCount: number
  /** Fade the example stack further (confirm step) to draw focus to the CTA. */
  exampleDimmed?: boolean
  /** Play the example stack's exit-upward animation (on confirm → loading). */
  exampleExiting?: boolean
}) {
  if (phase === 'loading') {
    // No card background — the loader's own screen-in (translateY 16px → 0 + fade)
    // reads as a slide-up-from-bottom, continuing the example stack's exit motion.
    return (
      <div className="flex min-h-[24rem] items-center justify-center">
        <AiAnalysisLoading onDone={onAnalysisDone} />
      </div>
    )
  }

  if (phase === 'results') {
    return <RealResults cards={cards} rangeBounds={rangeBounds} pendingCount={pendingCount} />
  }

  return <ExampleResults dimmed={exampleDimmed} exiting={exampleExiting} />
}

function ExampleResults({ dimmed, exiting }: { dimmed?: boolean; exiting?: boolean }) {
  const { cards, rangeBounds } = withRangeMeta(EXAMPLE_CARDS)
  return (
    <div
      className={cn(
        'relative',
        // On confirm → loading, the whole stack slides up and fades out. Otherwise
        // it eases toward its dim level (further faded on the confirm step).
        exiting
          ? 'motion-safe:animate-out fade-out-0 slide-out-to-top-6 fill-mode-forwards duration-300'
          : cn('transition-opacity duration-500', dimmed ? 'opacity-40' : 'opacity-100')
      )}
    >
      {/* Non-interactive preview. Each card fades progressively down the stack so
          the bottom two or three trail off — a sample, not a real quote. Cards
          animate in from the bottom on landing, lightly staggered. */}
      <div className="pointer-events-none flex flex-col gap-3 select-none" aria-hidden>
        {cards.map((card, i) => (
          <div
            key={card.id}
            className="motion-safe:animate-in fade-in-0 slide-in-from-bottom-4 fill-mode-both duration-500"
            style={{ opacity: Math.max(0.12, 0.92 - i * 0.26), animationDelay: `${i * 80}ms` }}
          >
            <CarrierResultCard card={card} rangeBounds={rangeBounds} billing="annual" />
          </div>
        ))}
      </div>
      {/* "EXAMPLES" rubber stamp in the upper-right corner, at a slight angle.
          top-20 clears the first card's "Likely eligible" label. */}
      <img
        src={examplesStamp}
        alt="Examples"
        className="pointer-events-none absolute top-20 right-2 w-56 max-w-[60%] -rotate-6 select-none"
      />
    </div>
  )
}

function RealResults({
  cards,
  rangeBounds,
  pendingCount,
}: {
  cards: CarrierCard[]
  rangeBounds: { min: number; max: number } | null
  pendingCount: number
}) {
  const [billing, setBilling] = useState<'annual' | 'monthly'>('monthly')

  // Terminal no-results state: every carrier declined / errored / was unavailable.
  if (cards.length === 0) {
    return (
      <div className="text-muted-foreground py-16 text-center text-sm">
        None of our carriers were able to offer an estimate for this DOT right now.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">
        <SegmentedControl
          value={billing}
          onValueChange={(v) => setBilling(v as 'annual' | 'monthly')}
          options={[
            { value: 'annual', label: 'Annual' },
            { value: 'monthly', label: 'Monthly' },
          ]}
        />
      </div>

      {/* Cards slide up + fade in, lightly staggered — the same load-in the
          example stack uses. Plays once on mount, not on billing toggle. */}
      <div className="flex flex-col gap-3">
        {cards.map((card, i) => (
          <div
            key={card.id}
            className="motion-safe:animate-in fade-in-0 slide-in-from-bottom-4 fill-mode-both duration-500"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <CarrierResultCard card={card} rangeBounds={rangeBounds} billing={billing} />
          </div>
        ))}
      </div>

      {pendingCount > 0 && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Spinner className="size-4" />
          <span>
            Still waiting on {pendingCount} {pendingCount === 1 ? 'carrier' : 'carriers'}…
          </span>
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        Price ranges are estimates for Auto Liability coverage. Complete an application for accurate quotes
        across multiple insurance providers and coverages.
      </p>
    </div>
  )
}
